/**
 * Documents API Service
 *
 * Handles document upload, retrieval, deletion, and management
 */

import { apiClient } from './client';
import { Document } from '../../types';
import { RedlineSegmentsResponse } from './negotiationApi';

class DocumentsAPI {
  async uploadDocument(projectId: string, file: File, onUploadProgress?: (progress: number) => void): Promise<Document> {
    // Validate file before upload
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const allowedExtensions = ['.pdf', '.docx'];

    if (file.size > maxSize) {
      throw new Error(`File "${file.name}" is too large. Maximum size is 100MB.`);
    }

    const isValidType = allowedTypes.includes(file.type) ||
      allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      throw new Error(`File "${file.name}" is not supported. Please upload PDF or Word documents only.`);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post<{message: string, document: Document}>(
        `/api/v1/projects/${projectId}/documents`,
        formData,
        {
          headers: {
            // Remove Content-Type to let browser set it with boundary
            'Content-Type': undefined,
          },
          onUploadProgress: onUploadProgress ? (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onUploadProgress(progress);
            }
          } : undefined,
          timeout: 300000, // 5 minutes for large files
        }
      );
      return response.data.document; // Return just the document part
    } catch (error: any) {
      if (error.response?.status === 413) {
        throw new Error('File is too large. Please upload a smaller file.');
      } else if (error.response?.status === 415) {
        throw new Error('File type not supported. Please upload PDF or Word documents only.');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out. Please try again with a smaller file.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async getDocuments(projectId: string): Promise<Document[]> {
    const response = await apiClient.get<{documents: Document[], total_count: number, total_size_bytes: number}>(`/api/v1/projects/${projectId}/documents`);
    return Array.isArray(response.data?.documents) ? response.data.documents : [];
  }

  // Get a single document by ID (Task 202)
  async getDocument(documentId: string): Promise<Document> {
    const response = await apiClient.get<Document>(`/api/v1/documents/${documentId}`);
    return response.data;
  }

  async deleteDocument(documentId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/documents/${documentId}`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Document not found or already deleted.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to delete this document.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  // Task 206: Update document category for Desk organization
  async updateDocumentCategory(documentId: string, category: string): Promise<Document> {
    try {
      const formData = new FormData();
      formData.append('category', category);

      const response = await apiClient.patch<Document>(
        `/api/v1/documents/${documentId}/category`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Document not found.');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Invalid category.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to update this document.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  // Task 207: Rename document
  async renameDocument(documentId: string, newName: string): Promise<Document> {
    try {
      const formData = new FormData();
      formData.append('new_name', newName);

      const response = await apiClient.patch<Document>(
        `/api/v1/documents/${documentId}/rename`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Document not found.');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Invalid document name.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to rename this document.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  }

  async getRedlineSegments(documentId: string) {
    return apiClient.get<RedlineSegmentsResponse>(
      `/api/v1/documents/${encodeURIComponent(documentId)}/redline-segments`
    );
  }

  // Plan 170: Comments layer threaded payload
  async getComments(documentId: string) {
    return apiClient.get<DocumentCommentsResponse>(
      `/api/v1/documents/${encodeURIComponent(documentId)}/comments`
    );
  }

  // Issue #861: re-enqueue clause-embedding generation for a doc whose
  // clause_embedding_status is failed/partial. Returns the new (pending) state.
  async reindexClauseEmbeddings(documentId: string) {
    return apiClient.post<{ document_id: string; clause_embedding_status: string; enqueued: boolean }>(
      `/api/v1/documents/${encodeURIComponent(documentId)}/reindex-clause-embeddings`
    );
  }
}

// Plan 170: response shape mirrors backend/app/routers/documents_comments.py
export interface DocumentCommentReply {
  id: string;
  author: string | null;
  comment_timestamp: string | null;
  text: string;
}

export interface DocumentComment {
  id: string;
  comment_id_in_docx: string | null;
  author: string | null;
  comment_timestamp: string | null;
  text: string;
  anchor_text: string | null;
  anchor_start: number | null;
  anchor_end: number | null;
  atom_start: number | null;
  atom_end: number | null;
  para_index: number | null;
  replies: DocumentCommentReply[];
}

export interface DocumentCommentsResponse {
  data: {
    // "_unmapped" is the well-known key for comments without a clause_id
    comments_by_clause: Record<string, DocumentComment[]>;
    total_threads: number;
    total_messages: number;
  };
}

export const documentsApi = new DocumentsAPI();
